import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const ClientCreateModal = ({
    isOpen,
    onClose,
    createForm,
    handleCreateChange,
    handleCreateSubmit,
    propertyTypes,
    cities,
    uploadProgress,
    supportedCurrencies,
    handleAddSubarea
}) => {
    const { t, i18n } = useTranslation();
    const [showAddArea, setShowAddArea] = useState(false);
    const [newAreaAr, setNewAreaAr] = useState('');
    const [newAreaEn, setNewAreaEn] = useState('');
    const [isAddingArea, setIsAddingArea] = useState(false);
    const [addAreaError, setAddAreaError] = useState('');

    const existingSubareas = cities?.find(c => c.en === createForm.city)?.subareas || [];
    const normalizedNewAr = newAreaAr.trim().toLowerCase();
    const normalizedNewEn = newAreaEn.trim().toLowerCase();

    const similarAreas = (newAreaAr.trim() || newAreaEn.trim())
        ? existingSubareas.filter(sub => {
            const arMatch = newAreaAr.trim() && sub.ar?.toLowerCase().includes(normalizedNewAr);
            const enMatch = newAreaEn.trim() && sub.en?.toLowerCase().includes(normalizedNewEn);
            return arMatch || enMatch;
          })
        : [];

    const isDuplicate = existingSubareas.some(sub => 
        sub.ar?.trim().toLowerCase() === normalizedNewAr ||
        (newAreaEn.trim() && sub.en?.trim().toLowerCase() === normalizedNewEn)
    );

    if (!isOpen) return null;


    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-bg-card border border-gold/20 rounded-lg shadow-2xl max-w-4xl w-full my-8">
                <div className="p-6 border-b border-gold/10">
                    <h3 className="text-2xl font-bold text-text">{t('createNewListing') || 'Create New Listing'}</h3>
                </div>

                <form onSubmit={handleCreateSubmit} className="p-6 space-y-6 max-h-[calc(100vh-16rem)] overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-muted mb-1">{t('propertyName')} <span className="text-danger-500">*</span></label>
                            <input
                                type="text"
                                name="title"
                                value={createForm.title}
                                onChange={handleCreateChange}
                                className="w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-muted mb-1">{t('propertyId')} <span className="text-danger-500">*</span></label>
                            <input
                                type="text"
                                name="property_id"
                                value={createForm.property_id}
                                onChange={handleCreateChange}
                                className="w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-muted mb-1">{t('price')}</label>
                            <input
                                type="number"
                                name="price"
                                value={createForm.price}
                                onChange={handleCreateChange}
                                className="w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-muted mb-1">{t('currency')}</label>
                            <select
                                name="currency"
                                value={createForm.currency}
                                onChange={handleCreateChange}
                                className="w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"
                            >
                                {supportedCurrencies && supportedCurrencies.length > 0 ? (
                                    (supportedCurrencies || []).map(currency => (
                                        <option key={currency} value={currency}>{t(`currency${currency}`) || currency}</option>
                                    ))
                                ) : (
                                    <option value="">{t('selectCurrency')}</option>
                                )}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-muted mb-1">{t('propertyType')}</label>
                            <select
                                name="property_type"
                                value={createForm.property_type}
                                onChange={handleCreateChange}
                                className="w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"
                            >
                                <option value="">{t('selectPropertyType')}</option>
                                {(propertyTypes || []).map(type => (
                                    <option key={type.en} value={type.en}>
                                        {i18n.language === 'ar' ? type.ar : (i18n.language === 'es' && type.es ? type.es : type.en)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-muted mb-1">{t('size')}</label>
                            <input
                                type="text"
                                name="size"
                                value={createForm.size}
                                onChange={handleCreateChange}
                                className="w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-muted mb-1">{t('rooms')}</label>
                            <input
                                type="number"
                                name="rooms"
                                value={createForm.rooms}
                                onChange={handleCreateChange}
                                className="w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"
                                min="0"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-muted mb-1">{t('floor')}</label>
                            <input
                                type="number"
                                name="floor"
                                value={createForm.floor}
                                onChange={handleCreateChange}
                                className="w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-muted mb-1">{t('city')}</label>
                            <select
                                name="city"
                                value={createForm.city}
                                onChange={handleCreateChange}
                                className="w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"
                            >
                                <option value="">{t('selectCity')}</option>
                                {(cities || []).map(city => (
                                    <option key={city.en} value={city.en}>
                                        {i18n.language === 'ar' ? city.ar : (i18n.language === 'es' && city.es ? city.es : city.en)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-sm font-medium text-text-muted">{t('area')}</label>
                                {createForm.city && !showAddArea && (
                                    <button
                                        type="button"
                                        onClick={() => setShowAddArea(true)}
                                        className="text-xs text-gold hover:underline"
                                    >
                                        + {i18n.language === 'ar' ? 'إضافة منطقة جديدة' : 'Add new area'}
                                    </button>
                                )}
                            </div>
                            {showAddArea ? (
                                <div className="p-3 bg-bg-soft border border-gold/20 rounded-md space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-[10px] font-medium text-text-muted mb-0.5">
                                                {i18n.language === 'ar' ? 'الاسم بالعربية' : 'Name in Arabic'}
                                            </label>
                                            <input
                                                type="text"
                                                value={newAreaAr}
                                                onChange={(e) => setNewAreaAr(e.target.value)}
                                                className="w-full bg-bg-card border border-gold/20 text-text rounded-md py-1 px-2 text-xs focus:ring-gold focus:border-gold"
                                                placeholder="الميدان"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-medium text-text-muted mb-0.5">
                                                {i18n.language === 'ar' ? 'الاسم بالإنجليزية' : 'Name in English'}
                                            </label>
                                            <input
                                                type="text"
                                                value={newAreaEn}
                                                onChange={(e) => setNewAreaEn(e.target.value)}
                                                className="w-full bg-bg-card border border-gold/20 text-text rounded-md py-1 px-2 text-xs focus:ring-gold focus:border-gold"
                                                placeholder="Midan"
                                            />
                                        </div>
                                    </div>
                                    {similarAreas.length > 0 && (
                                        <div className="text-[10px] text-text-muted bg-bg-card/50 p-2 border border-gold/10 rounded">
                                            <span className="font-semibold block mb-1">
                                                {i18n.language === 'ar' ? 'المناطق الموجودة المشابهة:' : 'Similar existing areas:'}
                                            </span>
                                            <div className="flex flex-wrap gap-1">
                                                {similarAreas.map(sub => (
                                                    <span key={sub.en} className="bg-bg-soft px-1.5 py-0.5 rounded text-gold border border-gold/10">
                                                        {i18n.language === 'ar' ? sub.ar : sub.en}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {isDuplicate && (
                                        <p className="text-[10px] text-danger-500">
                                            {i18n.language === 'ar' ? 'هذه المنطقة موجودة بالفعل' : 'This area already exists'}
                                        </p>
                                    )}
                                    {addAreaError && <p className="text-[10px] text-danger-500">{addAreaError}</p>}
                                    <div className="flex justify-end gap-2 text-xs">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowAddArea(false);
                                                setNewAreaAr('');
                                                setNewAreaEn('');
                                                setAddAreaError('');
                                            }}
                                            className="px-2 py-0.5 border border-gold/20 rounded text-text-muted hover:bg-gold/10 text-xs"
                                        >
                                            {t('cancel')}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={isAddingArea || !newAreaAr.trim() || isDuplicate}
                                            onClick={async () => {
                                                try {
                                                    setIsAddingArea(true);
                                                    setAddAreaError('');
                                                    const added = await handleAddSubarea(createForm.city, newAreaAr, newAreaEn);
                                                    handleCreateChange({
                                                        target: {
                                                            name: 'area',
                                                            value: added.en
                                                        }
                                                    });
                                                    setShowAddArea(false);
                                                    setNewAreaAr('');
                                                    setNewAreaEn('');
                                                } catch (err) {
                                                    setAddAreaError(err.response?.data?.detail || 'Failed to add area');
                                                } finally {
                                                    setIsAddingArea(false);
                                                }
                                            }}
                                            className="bg-gold text-white px-2 py-0.5 rounded hover:opacity-90 disabled:opacity-50 text-xs"
                                        >
                                            {isAddingArea ? '...' : (i18n.language === 'ar' ? 'حفظ' : 'Save')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                cities?.find(c => c.en === createForm.city)?.subareas?.length > 0 ? (
                                    <select
                                        name="area"
                                        value={createForm.area}
                                        onChange={handleCreateChange}
                                        className="w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"
                                    >
                                        <option value="">{t('selectArea') || 'Select Area'}</option>
                                        {cities.find(c => c.en === createForm.city).subareas.map(sub => (
                                            <option key={sub.en} value={sub.en}>
                                                {i18n.language === 'ar' ? sub.ar : (i18n.language === 'es' && sub.es ? sub.es : sub.en)}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        name="area"
                                        value={createForm.area}
                                        onChange={handleCreateChange}
                                        className="w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"
                                        placeholder={createForm.city ? (i18n.language === 'ar' ? "اكتب المنطقة أو أضفها" : "Type area or add it") : ""}
                                    />
                                )
                            )}
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                name="has_elevator"
                                checked={createForm.has_elevator}
                                onChange={handleCreateChange}
                                className="h-4 w-4 text-gold border-gold/30 rounded bg-bg-card focus:ring-gold"
                            />
                            <label className="ml-2 block text-sm text-text-muted">{t('hasElevator')}</label>
                        </div>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                name="has_solar"
                                checked={createForm.has_solar}
                                onChange={handleCreateChange}
                                className="h-4 w-4 text-gold border-gold/30 rounded bg-bg-card focus:ring-gold"
                            />
                            <label className="ml-2 block text-sm text-text-muted">{t('hasSolar')}</label>
                        </div>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                name="show_price"
                                checked={createForm.show_price}
                                onChange={handleCreateChange}
                                className="h-4 w-4 text-gold border-gold/30 rounded bg-bg-card focus:ring-gold"
                            />
                            <label className="ml-2 block text-sm text-text-muted">{t('showPrices')}</label>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-text-muted mb-1">{t('description')}</label>
                        <textarea
                            name="description"
                            value={createForm.description}
                            onChange={handleCreateChange}
                            rows="3"
                            className="w-full bg-bg-soft border border-gold/20 text-text rounded-md shadow-sm py-2 px-3 focus:ring-gold focus:border-gold"
                        ></textarea>
                        <p className="text-xs text-text-muted mt-1 italic">
                            {i18n.language === 'ar' ? 'استخدم **النص** لجعل الخط عريضاً' : 'Use **text** for bold'}
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-text-muted mb-1">
                            {t('propertyImages')}
                        </label>
                        <input
                            type="file"
                            name="property_images"
                            accept="image/*"
                            multiple
                            onChange={handleCreateChange}
                            className="w-full bg-bg-soft border border-gold/20 text-text-muted rounded-md shadow-sm py-2 px-3 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gold-gradient file:text-white hover:file:opacity-90"
                        />
                        <p className="text-xs text-text-muted mt-1">{t('uploadImagesHint') || 'Upload multiple images for the gallery'}</p>
                        {createForm.property_images.length > 0 && (
                            <p className="text-sm text-success-600 mt-1">
                                {createForm.property_images.length} {createForm.property_images.length === 1 ? 'image' : 'images'} selected
                            </p>
                        )}
                    </div>

                    {uploadProgress > 0 && uploadProgress < 100 && (
                        <div className="w-full bg-bg-card border border-gold/20 rounded-full h-2">
                            <div
                                className="bg-gold h-2 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                            ></div>
                        </div>
                    )}
                </form>

                <div className="p-6 border-t border-gold/10 flex justify-end gap-3 bg-bg-card">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gold/20 rounded-md text-sm font-medium text-text-muted hover:bg-gold/10 hover:text-gold transition-colors"
                    >
                        {t('cancel')}
                    </button>
                    <button
                        type="submit"
                        onClick={handleCreateSubmit}
                        disabled={uploadProgress > 0 && uploadProgress < 100}
                        className="bg-gold-gradient text-white border-none py-2 px-4 rounded-md shadow-sm text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {uploadProgress > 0 && uploadProgress < 100 ? `${t('uploading')} ${uploadProgress}%` : t('createListing') || 'Create Listing'}
                    </button>
                </div>
            </div >
        </div >
    );
};

export default ClientCreateModal;
